import { bool, func, number, oneOfType, shape, string } from "prop-types";
import { useCallback, useRef } from "react";

import { useUserData } from "Flocknote/apiHooks";

import { useOpenSacramentModal } from "Boards/Cells/Dynamic/SacramentFields/useOpenSacramentModal";
import {
  useClearExtendedFieldResponses,
  useHandleUploadFile,
} from "ExtendedFields/API/queryHooks";

import FnrDismissibleError from "UI/FnrDismissibleError";
import FnrUploadButton from "UI/FnrUploadButton";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap";

const FileCell = ({
  value: {
    fileName,
    href,
    entityFieldResponseID: oldEntityFieldResponseID, // though...it should never change!
  },
  entityID,
  extendedFieldData: {
    networkAvailableFieldID,
    fieldTypeInstanceStructureGroupingID,
    defaultFieldTypeInstanceMapping,
    parentNetworkFieldID,
  },
  saveCellValue,
  columnID,
  canEdit,
}) => {
  const [{ canEditSacraments }] = useUserData();
  const isSacramentField = fieldTypeInstanceStructureGroupingID === 1;

  const { openSacramentModal } = useOpenSacramentModal({
    targetUserID: entityID,
    defaultFieldTypeInstanceMapping: defaultFieldTypeInstanceMapping,
    networkFieldID: parentNetworkFieldID,
  });

  const {
    handleFileUpload,
    isUploading,
    errorMessage,
    clearUploadErrorMessage,
    progressPercentage,
  } = useHandleUploadFile({
    targetUserID: entityID,
    networkFieldID: networkAvailableFieldID,
  });

  const { mutate: deleteFile } = useClearExtendedFieldResponses({
    targetUserID: entityID,
    networkFieldID: networkAvailableFieldID,
  });

  const onChange = useCallback(
    async (newFile) => {
      if (!newFile) {
        saveCellValue({
          columnID,
          entityID,
          value: {
            entityFieldResponseID: oldEntityFieldResponseID,
          },
          localOnly: true,
        });
        return;
      }
      const {
        entityFieldResponseID,
        success,
        response: { fileName: newFileName } = {},
      } = (await handleFileUpload(newFile)) || {};

      if (success) {
        saveCellValue({
          columnID,
          entityID,
          value: {
            fileName: newFileName,
            entityFieldResponseID,
          },
          localOnly: true,
        });
        return newFileName;
      }
      fileInputRef.current.value = "";
      return false; // tells input not to update
    },
    [
      columnID,
      entityID,
      handleFileUpload,
      oldEntityFieldResponseID,
      saveCellValue,
    ]
  );
  const fileInputRef = useRef();

  return (
    <div>
      <FnrDismissibleError
        errorMessage={errorMessage}
        onDismiss={clearUploadErrorMessage}
        compact
        xOnRight
      />
      <FnrHorizontalGap>
        <FnrUploadButton
          onChange={onChange}
          compact
          initValue={fileName}
          color="textSecondary"
          confirmDelete
          href={href}
          ref={fileInputRef}
          isLoading={isUploading}
          onDelete={deleteFile}
          progressPercentage={progressPercentage}
          readOnly={!canEdit || isSacramentField}
          onEditClick={canEditSacraments ? openSacramentModal : undefined}
        />
      </FnrHorizontalGap>
    </div>
  );
};

FileCell.propTypes = {
  value: oneOfType([shape({ fileName: string, href: string }), string]),
  entityID: number.isRequired,
  extendedFieldData: shape({
    networkAvailableFieldID: number,
    fieldTypeInstanceStructureGroupingID: number,
    defaultFieldTypeInstanceMapping: string,
    parentNetworkFieldID: number,
  }),
  saveCellValue: func.isRequired,
  columnID: number.isRequired,
  canEdit: bool,
  fieldTypeInstanceStructureGroupingID: number,
};

export default FileCell;
